import { computed, Injectable, signal } from '@angular/core';
import { CanvasObject, Point, Size } from '../models/canvas-object.model';
import { ToolType } from '../models/tool.model';
import { CANVAS_SIZES, CanvasSizeType } from '../models/canvas-size.model';

export interface CanvasState {
    objects: Map<string, CanvasObject>;
    selectedIds: Set<string>;
    clipboard: CanvasObject[];
    history: HistoryEntry[];
    historyIndex: number;
    zoom: number;
    pan: Point;
    gridVisible: boolean;
    gridSize: number;
    snapToGrid: boolean;
}

interface HistoryEntry {
    objects: Map<string, CanvasObject>;
    selectedIds: Set<string>;
    action: string;
}

@Injectable({
    providedIn: 'root'
})
export class CanvasService {
    private nextObjectId = 1;

    // State signals
    private objectsMap = signal<Map<string, CanvasObject>>(new Map());
    private selectedIdsSet = signal<Set<string>>(new Set());
    private clipboardData = signal<CanvasObject[]>([]);
    private historyStack = signal<HistoryEntry[]>([]);
    private historyIdx = signal<number>(-1);
    private zoomLevel = signal<number>(1);
    private panPosition = signal<Point>({ x: 0, y: 0 });
    private gridVisibility = signal<boolean>(false);
    private gridSizeValue = signal<number>(10);
    private snapToGridEnabled = signal<boolean>(false);
    private currentToolType = signal<ToolType>('select');
    private canvasSizeType = signal<CanvasSizeType>('visiting-card');
    private canvasSizeCustom = signal<Size>({ width: 800, height: 600 });
    private activeSide = signal<'front' | 'back'>('front');

    // Computed signals
    readonly objects = computed(() => Array.from(this.objectsMap().values()));
    readonly selectedIds = computed(() => Array.from(this.selectedIdsSet()));
    readonly selectedObjects = computed(() =>
        this.selectedIds().map(id => this.objectsMap().get(id)!).filter(Boolean)
    );
    readonly canUndo = computed(() => this.historyIdx() >= 0);
    readonly canRedo = computed(() => this.historyIdx() < this.historyStack().length - 1);
    readonly zoom = this.zoomLevel.asReadonly();
    readonly pan = this.panPosition.asReadonly();
    readonly gridVisible = this.gridVisibility.asReadonly();
    readonly gridSize = this.gridSizeValue.asReadonly();
    readonly snapToGrid = this.snapToGridEnabled.asReadonly();
    readonly currentTool = this.currentToolType.asReadonly();
    readonly canvasSizeTypeValue = this.canvasSizeType.asReadonly();
    readonly canvasCustomSize = this.canvasSizeCustom.asReadonly();
    readonly activeSideValue = this.activeSide.asReadonly();
    readonly canvasSize = computed(() => {
        const type = this.canvasSizeType();
        const size = CANVAS_SIZES.find(s => s.type === type);
        if (type === 'custom') {
            return this.canvasSizeCustom();
        }
        return size ? { width: size.width, height: size.height } : { width: 800, height: 600 };
    });
    readonly activeObjects = computed(() => {
        const side = this.activeSide();
        const isTwoSided = this.canvasSizeType() === 'visiting-card';
        return Array.from(this.objectsMap().values()).filter(obj => {
            if (!isTwoSided) return true;
            const objSide = obj.side || 'front';
            return objSide === side;
        });
    });

    addObject(obj: Partial<CanvasObject> & { type: CanvasObject['type'] }): string {
        const id = `obj-${this.nextObjectId++}`;
        let side = obj.side;
        if (!side && this.canvasSizeType() === 'visiting-card') {
            side = this.requestSideForNewObject();
        }
        const newObj: CanvasObject = {
            id,
            name: `${obj.type} ${id}`,
            position: obj.position || { x: 0, y: 0 },
            size: obj.size || { width: 100, height: 100 },
            transform: obj.transform || { rotation: 0, scaleX: 1, scaleY: 1 },
            opacity: obj.opacity ?? 1,
            visible: obj.visible ?? true,
            locked: obj.locked ?? false,
            zIndex: obj.zIndex ?? this.objects().length,
            side,
            ...obj
        } as CanvasObject;

        const newMap = new Map(this.objectsMap());
        newMap.set(id, newObj);
        this.objectsMap.set(newMap);
        if (side && side !== this.activeSide()) {
            this.activeSide.set(side);
            this.clearSelection();
        }
        this.saveHistory('Add ' + obj.type);
        return id;
    }

    getObject(id: string): CanvasObject | undefined {
        return this.objectsMap().get(id);
    }

    updateObject(id: string, updates: Partial<CanvasObject>, skipHistory = false): void {
        const obj = this.objectsMap().get(id);
        if (!obj) return;

        const updatedObj = { ...obj, ...updates } as CanvasObject;
        const newMap = new Map(this.objectsMap());
        newMap.set(id, updatedObj);
        this.objectsMap.set(newMap);
        if (!skipHistory) {
            this.saveHistory('Update object');
        }
    }

    deleteObjects(ids: string[]): void {
        const newMap = new Map(this.objectsMap());
        ids.forEach(id => newMap.delete(id));
        this.objectsMap.set(newMap);

        const newSelected = new Set(this.selectedIdsSet());
        ids.forEach(id => newSelected.delete(id));
        this.selectedIdsSet.set(newSelected);

        this.saveHistory('Delete objects');
    }

    selectObject(id: string, addToSelection = false): void {
        const newSelected = addToSelection ? new Set(this.selectedIdsSet()) : new Set<string>();
        newSelected.add(id);
        this.selectedIdsSet.set(newSelected);
    }

    selectObjects(ids: string[]): void {
        this.selectedIdsSet.set(new Set(ids));
    }

    clearSelection(): void {
        this.selectedIdsSet.set(new Set());
    }

    duplicateSelected(): void {
        const selected = this.selectedObjects();
        const newIds: string[] = [];

        selected.forEach(obj => {
            const duplicate = {
                ...obj,
                position: { x: obj.position.x + 10, y: obj.position.y + 10 }
            };
            delete (duplicate as any).id;
            const newId = this.addObject(duplicate);
            newIds.push(newId);
        });

        this.selectObjects(newIds);
        this.saveHistory('Duplicate objects');
    }

    copySelected(): void {
        this.clipboardData.set([...this.selectedObjects()]);
    }

    paste(): void {
        const clipboard = this.clipboardData();
        if (clipboard.length === 0) return;

        const newIds: string[] = [];
        clipboard.forEach(obj => {
            const paste = {
                ...obj,
                position: { x: obj.position.x + 20, y: obj.position.y + 20 }
            };
            delete (paste as any).id;
            const newId = this.addObject(paste);
            newIds.push(newId);
        });

        this.selectObjects(newIds);
        this.saveHistory('Paste objects');
    }

    groupSelected(): void {
        const selected = this.selectedObjects();
        if (selected.length < 2) return;

        const bounds = this.getObjectsBounds(selected);
        const groupId = this.addObject({
            type: 'group',
            position: bounds.position,
            size: bounds.size,
            children: selected.map(obj => obj.id)
        } as any);

        // Update parent references
        selected.forEach(obj => {
            this.updateObject(obj.id, { parentId: groupId });
        });

        this.selectObject(groupId);
        this.saveHistory('Group objects');
    }

    ungroupSelected(): void {
        const selected = this.selectedObjects();
        const groups = selected.filter(obj => obj.type === 'group');

        groups.forEach(group => {
            if (group.type === 'group') {
                group.children.forEach(childId => {
                    this.updateObject(childId, { parentId: undefined });
                });
                this.deleteObjects([group.id]);
            }
        });

        this.saveHistory('Ungroup objects');
    }

    bringToFront(ids: string[]): void {
        const maxZ = Math.max(...this.objects().map(obj => obj.zIndex));
        ids.forEach((id, index) => {
            this.updateObject(id, { zIndex: maxZ + index + 1 });
        });
        this.saveHistory('Bring to front');
    }

    sendToBack(ids: string[]): void {
        const minZ = Math.min(...this.objects().map(obj => obj.zIndex));
        ids.forEach((id, index) => {
            this.updateObject(id, { zIndex: minZ - ids.length + index });
        });
        this.saveHistory('Send to back');
    }

    addText(position: Point, content: string): CanvasObject {
        const id = this.addObject({
            type: 'text',
            name: 'text',
            position,
            size: { width: 200, height: 32 },
            transform: { rotation: 0, scaleX: 1, scaleY: 1 },
            opacity: 1,
            visible: true,
            locked: false,
            content,
            fill: { r: 0, g: 0, b: 0, a: 1 },
            textStyle: {
                fontFamily: 'Arial',
                fontSize: 16,
                fontWeight: 'normal',
                fontStyle: 'normal',
                textAlign: 'left',
                textDecoration: 'none',
                lineHeight: 1.5,
                letterSpacing: 0
            }
        } as CanvasObject);

        return this.getObject(id)!;
    }

    setZoom(zoom: number): void {
        this.zoomLevel.set(Math.max(0.1, Math.min(10, zoom)));
    }

    setPan(pan: Point): void {
        this.panPosition.set(pan);
    }

    setGridVisible(visible: boolean): void {
        this.gridVisibility.set(visible);
    }

    setGridSize(size: number): void {
        this.gridSizeValue.set(size);
    }

    setSnapToGrid(snap: boolean): void {
        this.snapToGridEnabled.set(snap);
    }

    setCurrentTool(tool: ToolType): void {
        this.currentToolType.set(tool);
    }

    setCanvasSize(type: CanvasSizeType): void {
        this.canvasSizeType.set(type);
        if (type !== 'visiting-card') {
            this.activeSide.set('front');
            this.clearSelection();
        }
    }

    setCanvasCustomSize(size: Size): void {
        this.canvasSizeCustom.set(size);
        this.canvasSizeType.set('custom');
        this.activeSide.set('front');
        this.clearSelection();
    }

    setActiveSide(side: 'front' | 'back'): void {
        this.activeSide.set(side);
        this.clearSelection();
    }

    requestSideForNewObject(): 'front' | 'back' {
        return this.activeSide();
    }

    saveHistoryNow(action: string): void {
        this.saveHistory(action);
    }

    private saveHistory(action: string): void {
        const entry: HistoryEntry = {
            objects: new Map(this.objectsMap()),
            selectedIds: new Set(this.selectedIdsSet()),
            action
        };

        const history = this.historyStack().slice(0, this.historyIdx() + 1);
        history.push(entry);

        // Limit history to 50 entries
        if (history.length > 50) {
            history.shift();
        }

        this.historyStack.set(history);
        this.historyIdx.set(history.length - 1);
    }

    undo(): void {
        if (!this.canUndo()) return;

        const newIdx = this.historyIdx() - 1;
        if (newIdx >= 0) {
            const entry = this.historyStack()[newIdx];
            this.objectsMap.set(new Map(entry.objects));
            this.selectedIdsSet.set(new Set(entry.selectedIds));
        } else {
            this.objectsMap.set(new Map());
            this.selectedIdsSet.set(new Set());
        }
        this.historyIdx.set(newIdx);
    }

    redo(): void {
        if (!this.canRedo()) return;

        const newIdx = this.historyIdx() + 1;
        const entry = this.historyStack()[newIdx];
        this.objectsMap.set(new Map(entry.objects));
        this.selectedIdsSet.set(new Set(entry.selectedIds));
        this.historyIdx.set(newIdx);
    }

    clear(): void {
        this.objectsMap.set(new Map());
        this.selectedIdsSet.set(new Set());
        this.historyStack.set([]);
        this.historyIdx.set(-1);
        this.nextObjectId = 1;
    }

    private getObjectsBounds(objects: CanvasObject[]): { position: Point; size: Size } {
        if (objects.length === 0) {
            return { position: { x: 0, y: 0 }, size: { width: 0, height: 0 } };
        }

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        objects.forEach(obj => {
            minX = Math.min(minX, obj.position.x);
            minY = Math.min(minY, obj.position.y);
            maxX = Math.max(maxX, obj.position.x + obj.size.width);
            maxY = Math.max(maxY, obj.position.y + obj.size.height);
        });

        return {
            position: { x: minX, y: minY },
            size: { width: maxX - minX, height: maxY - minY }
        };
    }

    alignSelected(mode: 'left' | 'right' | 'h-center' | 'top' | 'bottom' | 'v-center'): void {
        const selected = this.selectedObjects();
        if (selected.length < 2) return;

        const bounds = this.getObjectsBounds(selected);
        const left = bounds.position.x;
        const right = bounds.position.x + bounds.size.width;
        const top = bounds.position.y;
        const bottom = bounds.position.y + bounds.size.height;
        const centerX = bounds.position.x + bounds.size.width / 2;
        const centerY = bounds.position.y + bounds.size.height / 2;

        selected.forEach(obj => {
            const nextPosition = { ...obj.position };
            switch (mode) {
                case 'left':
                    nextPosition.x = left;
                    break;
                case 'right':
                    nextPosition.x = right - obj.size.width;
                    break;
                case 'h-center':
                    nextPosition.x = centerX - obj.size.width / 2;
                    break;
                case 'top':
                    nextPosition.y = top;
                    break;
                case 'bottom':
                    nextPosition.y = bottom - obj.size.height;
                    break;
                case 'v-center':
                    nextPosition.y = centerY - obj.size.height / 2;
                    break;
            }
            this.updateObject(obj.id, { position: nextPosition }, true);
        });

        this.saveHistory(`Align ${mode}`);
    }

    distributeSelected(axis: 'horizontal' | 'vertical', skipHistory = false): void {
        const selected = this.selectedObjects();
        if (selected.length < 3) return;

        const sorted = [...selected].sort((a, b) =>
            axis === 'horizontal' ? a.position.x - b.position.x : a.position.y - b.position.y
        );

        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const minEdge = axis === 'horizontal' ? first.position.x : first.position.y;
        const maxEdge = axis === 'horizontal'
            ? last.position.x + last.size.width
            : last.position.y + last.size.height;

        const totalSize = sorted.reduce((sum, obj) =>
                sum + (axis === 'horizontal' ? obj.size.width : obj.size.height),
            0
        );

        const available = Math.max(0, maxEdge - minEdge - totalSize);
        const gap = available / (sorted.length - 1);

        let cursor = minEdge;
        sorted.forEach(obj => {
            const nextPosition = { ...obj.position };
            if (axis === 'horizontal') {
                nextPosition.x = cursor;
                cursor += obj.size.width + gap;
            } else {
                nextPosition.y = cursor;
                cursor += obj.size.height + gap;
            }
            this.updateObject(obj.id, { position: nextPosition }, true);
        });

        if (!skipHistory) {
            this.saveHistory(`Distribute ${axis}`);
        }
    }

    tidyUpSelected(): void {
        const selected = this.selectedObjects();
        if (selected.length < 3) return;

        this.distributeSelected('horizontal', true);
        this.distributeSelected('vertical', true);
        this.saveHistory('Tidy up');
    }

    snapToGridPoint(point: Point): Point {
        if (!this.snapToGridEnabled()) return point;
        const gridSize = this.gridSizeValue();
        return {
            x: Math.round(point.x / gridSize) * gridSize,
            y: Math.round(point.y / gridSize) * gridSize
        };
    }

    updateSVGLayer(objectId: string, layerId: string, fill?: string, stroke?: string): void {
        const obj = this.getObject(objectId);
        if (!obj || obj.type !== 'svg') return;

        const svgObj = obj as any;
        if (!svgObj.layers) return;

        // Update layer color
        const layer = svgObj.layers.find((l: any) => l.id === layerId);
        if (!layer) return;

        if (fill !== undefined) layer.fill = fill;
        if (stroke !== undefined) layer.stroke = stroke;

        // Parse and update SVG content
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgObj.svgContent, 'image/svg+xml');
        const element = svgDoc.getElementById(layerId);

        if (element) {
            if (fill !== undefined && fill !== null) {
                element.setAttribute('fill', fill);
            }
            if (stroke !== undefined && stroke !== null) {
                element.setAttribute('stroke', stroke);
            }

            const svgElement = svgDoc.querySelector('svg');
            if (svgElement) {
                const updatedContent = svgElement.outerHTML;
                const updatedLayers = [...svgObj.layers];
                const layerIdx = updatedLayers.findIndex((l: any) => l.id === layerId);
                if (layerIdx >= 0) {
                    updatedLayers[layerIdx] = { ...layer };
                }

                // Update with new references to trigger change detection
                const newMap = new Map(this.objectsMap());
                newMap.set(objectId, {
                    ...obj,
                    svgContent: updatedContent,
                    layers: updatedLayers
                } as any);
                this.objectsMap.set(newMap);
                this.saveHistory('Update SVG layer');
            }
        }
    }

    clearSVGCache(objectId: string): void {
        // This method can be called to clear the SVG rendering cache if needed
        // For now, this is a placeholder for future optimization
    }
}
